import CallControlsButton from "@/components/common/videoCallUIComponents/CallControlButtons";
import { ChatBotPage } from "@/components/common/ChatBot";
import MeetingHeader from "@/components/common/videoCallUIComponents/MeetingHeader";
import ParticipantDisplay from "@/components/common/videoCallUIComponents/ParticipantDisplay";
import { ParticipantsListPage } from "@/components/common/ParticipantsListPage";
import ParticipantThumbnail from "@/components/common/videoCallUIComponents/ParticipantThumbnail";
import Whiteboard from "@/components/common/Whiteboard";
// import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
// import { Button } from "@/components/ui/button";
import type { RootState } from "@/store";
import { type Participant } from "@/store/slices/callSlice";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PiPWrapper from "@/components/common/PIPPage";

export interface VideoCallUIProps {
  localStream: MediaStream | null;
  peers: Participant[];
  // connectionStatus: string;
  userId: string | null;
  // chatMessages: Array<{ sender: string; message: string; timestamp: number }>;
  // sendMessage: (message: string) => void;
  toggleLocalMic: () => void;
  toggleLocalVideo: () => void;
  toggleScreenShare: () => void;
  localMicOn: boolean;
  localVideoOn: boolean;
  screenShareActive: boolean;
  activeSpeakerInfo: Participant | null;
  activeSpeakerStream: MediaStream | null;
  handleDeviceChange: (
    deviceId: string,
    type: "audio" | "video"
  ) => Promise<void>;
  handleFacingModeChange: (facingMode: "user" | "environment") => void;
  selectedMic: string;
  selectedCamera: string;
  // availableMics: MediaDeviceInfo[];
  // availableCameras: MediaDeviceInfo[];
  disconnect: () => void;
  // areStreamsReady: boolean;
  setChatMessageReceivedCallback: (callback: () => void) => void;
  sendRtcChatMessage: (message: string) => void;
  sendRtcDataMessage: (messageType: string, data: any) => void;
}

// Define a type for participants in the thumbnail list, extending Participant
// and adding the 'mediaStream' property that ParticipantThumbnail expects.
type ThumbnailParticipant = Participant & {
  mediaStream: MediaStream | null;
  isScreenShareThumbnail?: boolean;
};

const useWindowSize = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};
const VideoCallUI: React.FC<VideoCallUIProps> = ({
  localStream,
  peers,
  // connectionStatus,
  userId,
  // chatMessages,
  // sendMessage,
  toggleLocalMic,
  toggleLocalVideo,
  toggleScreenShare,
  localMicOn,
  localVideoOn,
  screenShareActive,
  activeSpeakerInfo,
  // activeSpeakerStream,
  handleDeviceChange,
  handleFacingModeChange,
  selectedMic,
  selectedCamera,
  // availableMics,
  // availableCameras,
  disconnect,
  // areStreamsReady
  setChatMessageReceivedCallback,
  sendRtcChatMessage,
  sendRtcDataMessage,
}) => {
  // --- UI-specific states (not WebRTC related) ---
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsListOpen, setIsParticipantsListOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const AgentName = localStorage.getItem("AgentName") || "Unknown Agent";
  const isWhiteboardOpen = useSelector(
    (state: RootState) => state.call.isWhiteboardOpen
  );

  const [selfPosition, setSelfPosition] = useState({ x: 250, y: 144 });
  const [isDragging, setIsDragging] = useState(false);
  const selfVideoRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [showControls, setShowControls] = useState(true);
const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
const [isPiPActive, setIsPiPActive] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1020); // Tailwind's md breakpoint
    checkMobile(); // run once
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  const screenWidth = useWindowSize();

  // Define breakpoints
  const isMobileOrTablet = screenWidth <= 1020;
  // Ensure localParticipant always uses the correct userId from Redux
  const localParticipant: Participant = {
    id: userId || "you-self",
    name: "You",
    avatar: "https://placehold.co/150x150/13A8E1/FFFFFF?text=You",
    isMuted: !localMicOn,
    isVideoOff: !localVideoOn,
    cameraStream: localStream,

    screenShareStream:
      peers.find((p) => p.id === userId)?.screenShareStream || null,
    isScreenSharing: screenShareActive,
    // isActiveSpeaker: activeSpeakerInfo?.id === userId
  };

  const MAX_VISIBLE_THUMBNAILS = 3;

  // Filter peers to ensure the local user is represented by localParticipant and not duplicated
  const remoteParticipants = useMemo(() => {
    return (peers || []).filter((p) => p.id !== userId);
  }, [peers, userId]);
  console.log(remoteParticipants, " remoteParticipants in VideoCallUI");
  // Find the participant whose name is not "Guest"
  const agentParticipantID = remoteParticipants.find(
    (p) => p.name && p.name !== "Guest"
  );
  const agentName = agentParticipantID ? agentParticipantID.name : null;
  // console.log("Agent Name ID:", agentName);
  useEffect(() => {
    if (!localStream) return;

    // Update audio tracks based on localMicOn
    localStream.getAudioTracks().forEach((track) => {
      track.enabled = localMicOn;
    });

    // Update video tracks based on localVideoOn
    localStream.getVideoTracks().forEach((track) => {
      track.enabled = localVideoOn;
    });

    console.log(
      `Updated local tracks: Mic is ${localMicOn ? "ON" : "OFF"}, Video is ${
        localVideoOn ? "ON" : "OFF"
      }`
    );
  }, [localMicOn, localVideoOn, localStream]);

  useEffect(() => {
    const hasRefreshed = localStorage.getItem("videoCallPageRefreshed");

    if (!hasRefreshed) {
      const micState = localStream?.getAudioTracks()[0]?.enabled ? "on" : "off";
      localStorage.setItem("micStateBeforeReload", micState);
      localStorage.setItem("videoCallPageRefreshed", "true");
      window.location.reload();
    }
  }, []);
  useEffect(() => {
    const savedMicState = localStorage.getItem("micStateBeforeReload");

    if (savedMicState && localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = savedMicState === "on";
      }

      localStorage.removeItem("micStateBeforeReload");
    }
  }, [localStream]);
  // Construct the list of all participants for the participants list
  const allParticipants: Participant[] = [
    localParticipant,
    ...remoteParticipants,
  ];
  const someoneIsScreenSharing =
    (localParticipant.isScreenSharing &&
      !!localParticipant.screenShareStream) ||
    remoteParticipants.some((p) => p.isScreenSharing && !!p.screenShareStream);

  let mainStreamInView: MediaStream | null = null;
  let mainParticipantInView: Participant = localParticipant;

  // 1) Whiteboard takes precedence
  if (isWhiteboardOpen) {
    mainStreamInView = null;
    mainParticipantInView = { ...localParticipant, name: "Whiteboard" };
  } else {
    // 2) If anyone is screen sharing, prefer their screen (local first, then agent, then any other)
    if (
      localParticipant.isScreenSharing &&
      localParticipant.screenShareStream
    ) {
      mainParticipantInView = { ...localParticipant, name: "Your Screen" };
      mainStreamInView = localParticipant.screenShareStream;
    } else {
      const agentScreen = remoteParticipants.find(
        (p) => p.isScreenSharing && p.screenShareStream
      );
      if (agentScreen) {
        mainParticipantInView = agentScreen;
        mainStreamInView = agentScreen.screenShareStream;
      } else {
        // 3) If there are >= 2 remote participants and NO screen share,
        //    show the remote participants (agent + first other remote) in the main area (grid)
        //    otherwise fallback to single remote or local.
        const remWithCamera = remoteParticipants.filter(
          (p) => p.cameraStream && !p.isVideoOff
        );
        if (!someoneIsScreenSharing && remWithCamera.length >= 2) {
          // We'll render a two-column main grid (agent + another remote)
          mainParticipantInView = remWithCamera[0]; // primary remote for single-display fallback
          mainStreamInView = remWithCamera[0].cameraStream;
          // Note: actual two-column rendering handled below in JSX
        } else {
          // fallback: choose the first remote with camera, else local
          const firstRemotePeerWithCamera = remWithCamera[0];
          if (firstRemotePeerWithCamera) {
            mainParticipantInView = firstRemotePeerWithCamera;
            mainStreamInView = firstRemotePeerWithCamera.cameraStream;
          } else if (
            localParticipant.cameraStream &&
            !localParticipant.isVideoOff
          ) {
            mainParticipantInView = localParticipant;
            mainStreamInView = localStream;
          } else {
            mainParticipantInView = localParticipant;
            mainStreamInView = localStream;
          }
        }
      }
    }
  }

  // ensure local name is correct if showing local
  if (mainParticipantInView.id === userId && !isWhiteboardOpen) {
    mainParticipantInView = {
      ...localParticipant,
      name: localParticipant.isScreenSharing ? "Your Screen" : "You",
    };
  }

  const participantsForThumbnails: ThumbnailParticipant[] = [];

  if (isWhiteboardOpen) {
    // Whiteboard open -> default behavior
    if (localParticipant.cameraStream) {
      participantsForThumbnails.push({
        ...localParticipant,
        mediaStream: localParticipant.cameraStream,
        name: "You",
        isScreenSharing: false,
        isScreenShareThumbnail: false,
        isVideoOff: !localParticipant.cameraStream
          .getVideoTracks()
          .some((track) => track.enabled),
        isMuted: !localParticipant.cameraStream
          .getAudioTracks()
          .some((track) => track.enabled),
      });
    }

    const agentParticipant = remoteParticipants.find(
      (p) => p.name === AgentName
    );

    if (agentParticipant && agentParticipant.cameraStream) {
      participantsForThumbnails.push({
        ...agentParticipant,
        mediaStream: agentParticipant.cameraStream,
        name: agentParticipant.name,
        isScreenSharing: false,
        isScreenShareThumbnail: false,
      });
    }

    remoteParticipants.forEach((p) => {
      if (p.id !== agentParticipant?.id) {
        if (p.cameraStream) {
          participantsForThumbnails.push({
            ...p,
            mediaStream: p.cameraStream,
            name: p.name,
            isScreenSharing: false,
            isScreenShareThumbnail: false,
          });
        }
        if (p.screenShareStream && p.isScreenSharing) {
          participantsForThumbnails.push({
            ...p,
            mediaStream: p.screenShareStream,
            name: `${p.name}'s Screen`,
            isScreenSharing: true,
            isScreenShareThumbnail: true,
            isMuted: true,
            isVideoOff: false,
          });
        }
      }
    });
  } else {
    // ---------- No Whiteboard ----------
    const activeRemoteCameras = remoteParticipants.filter(
      (p) => p.cameraStream && !p.isVideoOff
    );

    if (!someoneIsScreenSharing && activeRemoteCameras.length >= 2) {
      if (isMobileOrTablet) {
        /**
         * Mobile / Tablet Behavior:
         * When 2+ remote participants and no screen share,
         * thumbnails show ONLY the local user's camera.
         */
        if (localParticipant.cameraStream) {
          participantsForThumbnails.push({
            ...localParticipant,
            mediaStream: localParticipant.cameraStream,
            name: "You",
            isScreenSharing: false,
            isScreenShareThumbnail: false,
            isVideoOff: !localParticipant.cameraStream
              .getVideoTracks()
              .some((track) => track.enabled),
            isMuted: !localParticipant.cameraStream
              .getAudioTracks()
              .some((track) => track.enabled),
          });
        }
      } else {
        /**
         * Desktop Behavior:
         * Even with 2+ remote participants,
         * include everyone in thumbnails so the 3rd expert is visible.
         */
        if (localParticipant.cameraStream) {
          participantsForThumbnails.push({
            ...localParticipant,
            mediaStream: localParticipant.cameraStream,
            name: "You",
            isScreenSharing: false,
            isScreenShareThumbnail: false,
            isVideoOff: !localParticipant.cameraStream
              .getVideoTracks()
              .some((track) => track.enabled),
            isMuted: !localParticipant.cameraStream
              .getAudioTracks()
              .some((track) => track.enabled),
          });
        }
        remoteParticipants.forEach((p) => {
          if (p.cameraStream) {
            participantsForThumbnails.push({
              ...p,
              mediaStream: p.cameraStream,
              name: p.name,
              isScreenSharing: false,
              isScreenShareThumbnail: false,
            });
          }
        });
      }
    } else if (someoneIsScreenSharing) {
      /**
       * When screen share is active:
       * Always include local camera + all remote cameras + any screen share streams.
       */
      if (localParticipant.cameraStream) {
        participantsForThumbnails.push({
          ...localParticipant,
          mediaStream: localParticipant.cameraStream,
          name: "You",
          isScreenSharing: false,
          isScreenShareThumbnail: false,
          isVideoOff: !localParticipant.cameraStream
            .getVideoTracks()
            .some((track) => track.enabled),
          isMuted: !localParticipant.cameraStream
            .getAudioTracks()
            .some((track) => track.enabled),
        });
      }

      remoteParticipants.forEach((p) => {
        if (p.cameraStream) {
          participantsForThumbnails.push({
            ...p,
            mediaStream: p.cameraStream,
            name: p.name,
            isScreenSharing: false,
            isScreenShareThumbnail: false,
          });
        }
        if (p.screenShareStream && p.isScreenSharing) {
          participantsForThumbnails.push({
            ...p,
            mediaStream: p.screenShareStream,
            name: `${p.name}'s Screen`,
            isScreenSharing: true,
            isScreenShareThumbnail: true,
            isMuted: true,
            isVideoOff: false,
          });
        }
      });
    } else {
      /**
       * Default behavior: 1-on-1 or single remote.
       * Include local + all remote cameras.
       */
      if (localParticipant.cameraStream) {
        participantsForThumbnails.push({
          ...localParticipant,
          mediaStream: localParticipant.cameraStream,
          name: "You",
          isScreenSharing: false,
          isScreenShareThumbnail: false,
          isVideoOff: !localParticipant.cameraStream
            .getVideoTracks()
            .some((track) => track.enabled),
          isMuted: !localParticipant.cameraStream
            .getAudioTracks()
            .some((track) => track.enabled),
        });
      }

      remoteParticipants.forEach((p) => {
        if (p.cameraStream) {
          participantsForThumbnails.push({
            ...p,
            mediaStream: p.cameraStream,
            name: p.name,
            isScreenSharing: false,
            isScreenShareThumbnail: false,
          });
        }
      });
    }
  }

  const finalThumbnails = participantsForThumbnails.filter((p) => {
    return !(
      p.id === mainParticipantInView.id && p.mediaStream === mainStreamInView
    );
  });

  const visibleThumbnails = finalThumbnails.slice(0, MAX_VISIBLE_THUMBNAILS);
  // const hiddenParticipantsCount = Math.max(
  //   0,
  //   finalThumbnails.length - MAX_VISIBLE_THUMBNAILS
  // );
  // const hiddenParticipantsAvatars = finalThumbnails.slice(
  //   MAX_VISIBLE_THUMBNAILS,
  //   MAX_VISIBLE_THUMBNAILS + 3
  // );

  // --- UI Event Handlers (now call functions from props) ---
  const handleLeaveMeeting = useCallback(() => {
    disconnect();
  }, [disconnect]);

  const enablePiP = async (stream: MediaStream) => {
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Request Picture-in-Picture
    await video.requestPictureInPicture();
    setIsPiPActive(true);
    console.log("PIP", isPiPActive)
  } catch (err) {
    console.error("Failed to enter PiP:", err);
  }
};
const handleOpenChat = useCallback(() => {
  const newValue = !isChatOpen;
  setIsChatOpen(newValue);

  if (newValue) {
    // When chat is opened
    setIsParticipantsListOpen(false);
    setUnreadMessageCount(0);

    if (isMobileOrTablet && mainStreamInView) {
      enablePiP(mainStreamInView);
    }
  } else {
    // When chat is closed
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch((err) => {
        console.warn("Error exiting PiP:", err);
      });
    }
  }
}, [isChatOpen, mainStreamInView, isMobileOrTablet]);

  const handleNewChatMessageReceived = useCallback(() => {
    if (!isChatOpen) {
      setUnreadMessageCount((prevCount) => prevCount + 1);
    }
  }, [isChatOpen]);

  useEffect(() => {
    setChatMessageReceivedCallback(handleNewChatMessageReceived);
  }, [setChatMessageReceivedCallback, handleNewChatMessageReceived]);

  const handleToggleScreenShare = useCallback(async () => {
    await toggleScreenShare();
  }, [toggleScreenShare]);

  const handleOpenParticipantsList = useCallback(() => {
    setIsParticipantsListOpen(!isParticipantsListOpen);
    if (!isParticipantsListOpen) {
      setIsChatOpen(false);
    }
  }, [isParticipantsListOpen]);

  // --- UI Layout Logic ---
  const gridColsClass =
    isChatOpen || isParticipantsListOpen
      ? "grid-cols-2 lg:grid-cols-4   "
      : "grid-cols-1 lg:grid-cols-3 md:grid-cols-2 md:grid-rows-1";

  const mainViewColSpanClass =
    isChatOpen || isParticipantsListOpen ? "md:col-span-2" : "md:col-span-2";
  useEffect(() => {
    // Automatically turn on mic and video once user joins
    if (!localMicOn) toggleLocalMic();
    if (!localVideoOn) toggleLocalVideo();
  }, []);
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const clientX = e.type.includes("touch")
        ? (e as React.TouchEvent).touches[0].clientX
        : (e as React.MouseEvent).clientX;
      const clientY = e.type.includes("touch")
        ? (e as React.TouchEvent).touches[0].clientY
        : (e as React.MouseEvent).clientY;
      dragStart.current = {
        x: clientX - selfPosition.x,
        y: clientY - selfPosition.y,
      };
    },
    [selfPosition]
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.type.includes("touch")
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
      const clientY = e.type.includes("touch")
        ? (e as TouchEvent).touches[0].clientY
        : (e as MouseEvent).clientY;
      const newX = clientX - dragStart.current.x;
      const newY = clientY - dragStart.current.y;
      const container = containerRef.current?.getBoundingClientRect();
      const selfVideo = selfVideoRef.current?.getBoundingClientRect();
      if (container && selfVideo) {
        const clampedX = Math.min(
          Math.max(newX, 0),
          container.width - selfVideo.width
        );
        const clampedY = Math.min(
          Math.max(newY, 0),
          container.height - selfVideo.height
        );
        setSelfPosition({ x: clampedX, y: clampedY });
        requestAnimationFrame(() => {
          setSelfPosition({ x: clampedX, y: clampedY });
        });
      }
    },
    [isDragging]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (isDragging) {
      document.addEventListener("mousemove", handleDragMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleDragEnd, { passive: false });
      document.addEventListener("touchmove", handleDragMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleDragEnd, { passive: false });
    } else {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("touchend", handleDragEnd);
    }
    return () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    const handleRTCError = (event: any) => {
      const { message, error } = event.detail;
      console.error("RTC Error Event:", error);

      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    };

    window.addEventListener("rtc-error", handleRTCError);

    return () => {
      window.removeEventListener("rtc-error", handleRTCError);
    };
  }, []);
  const handleUserInteraction = () => {
  setShowControls(true);
  if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  hideTimerRef.current = setTimeout(() => setShowControls(false), 3000); // hides after 3 sec
};

useEffect(() => {
  document.addEventListener("click", handleUserInteraction);
  document.addEventListener("mousemove", handleUserInteraction);
  document.addEventListener("touchstart", handleUserInteraction);

  // hide after 3s initially
  hideTimerRef.current = setTimeout(() => setShowControls(false), 3000);

  return () => {
    document.removeEventListener("click", handleUserInteraction);
    document.removeEventListener("mousemove", handleUserInteraction);
    document.removeEventListener("touchstart", handleUserInteraction);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };
}, []);
  return (
    <div className="flex flex-col bg-blue-50 justify-center items-center h-full flex-grow">
      <div className="w-full max-w-full bg-blue-50 rounded-xl overflow-hidden flex flex-col h-[calc(100vh-32px)]">
        <div className={`${isMobile ? "landscape:hidden" : ""}`}>
          <MeetingHeader
            meetingName={`Meeting with CCE `}
            participantsCount={peers.length}
            onLeaveMeeting={handleLeaveMeeting}
            onOpenChat={handleOpenChat}
            unreadMessageCount={unreadMessageCount}
          />
        </div>

        <div
          className={`px-2 pt-0 w-full grid ${gridColsClass} gap-3 flex-grow min-h-0 ${
            isMobile ? "landscape:flex" : ""
          } `}
          ref={containerRef}
        >
          <div
            className={`${mainViewColSpanClass} flex flex-col relative flex-grow xl:min-h-0 md:min-h-0 min-h-[28rem] landscape:min-h-fit`}
          >
            {isWhiteboardOpen ? (
              <div className="w-full h-full">
                <Whiteboard sendRtcDataMessage={sendRtcDataMessage} />
              </div>
            ) : (
              // <ParticipantDisplay
              //   participantName={AgentName}
              //   avatarSrc={mainParticipantInView.avatar}
              //   isRecording={true}
              //   isScreenSharing={mainParticipantInView.isScreenSharing}
              //   stream={mainStreamInView}
              //   isActiveSpeaker={
              //     activeSpeakerInfo?.id === mainParticipantInView.id
              //   }
              //   isLocalUser={mainParticipantInView.id === userId}
              // />
              <>
                {/* CASE: Screen share active -> single big screen */}
                {someoneIsScreenSharing ? (
                  <PiPWrapper stream={mainStreamInView}>
                    <ParticipantDisplay
                      participantName={mainParticipantInView.name || AgentName}
                      avatarSrc={mainParticipantInView.avatar}
                      isRecording={true}
                      isScreenSharing={mainParticipantInView.isScreenSharing}
                      stream={mainStreamInView}
                      isActiveSpeaker={
                        activeSpeakerInfo?.id === mainParticipantInView.id
                      }
                      isLocalUser={mainParticipantInView.id === userId}
                    />
                  </PiPWrapper>
                ) : isMobileOrTablet &&
                  remoteParticipants.filter(
                    (p) => p.cameraStream && !p.isVideoOff
                  ).length >= 2 ? (
                  /* CASE: Mobile/Tablet with 2+ remote participants -> show them side-by-side */
                  <div className="grid lg:grid-rows-none landscape:grid-cols-2 landscape:h-screen landscape:absolute grid-rows-2 gap-2 w-full h-full">
                    {remoteParticipants
                      .filter((p) => p.cameraStream && !p.isVideoOff)
                      .slice(0, 2)
                      .map((p) => (
                        <PiPWrapper
                          key={p.id}
                          stream={p.cameraStream || p.screenShareStream}
                        >
                          <ParticipantDisplay
                            key={p.id}
                            participantName={p.name}
                            avatarSrc={p.avatar}
                            isRecording={false}
                            isScreenSharing={p.isScreenSharing}
                            stream={p.cameraStream || p.screenShareStream}
                            isActiveSpeaker={activeSpeakerInfo?.id === p.id}
                            isLocalUser={p.id === userId}
                          />
                        </PiPWrapper>
                      ))}
                  </div>
                ) : (
                  /* Default single participant view */
                  <PiPWrapper stream={mainStreamInView}>
                    <ParticipantDisplay
                      participantName={AgentName}
                      avatarSrc={mainParticipantInView.avatar}
                      isRecording={true}
                      isScreenSharing={mainParticipantInView.isScreenSharing}
                      stream={mainStreamInView}
                      isActiveSpeaker={
                        activeSpeakerInfo?.id === mainParticipantInView.id
                      }
                      isLocalUser={mainParticipantInView.id === userId}
                    />
                  </PiPWrapper>
                )}
              </>
            )}
          </div>

          {/* Other Participants Thumbnails */}
          {isMobileOrTablet && !isWhiteboardOpen ? (
            someoneIsScreenSharing ? (
              <div
                className={`md:col-span-1 flex md:flex-col gap-3 landscape:gap-2 md:h-auto h-44 overflow-y-auto pr-2 rounded-xl ${
                  isMobileOrTablet
                    ? " portrait:flex-row portrait:w-full landscape:flex landscape:flex-col landscape:w-60 landscape:h-auto"
                    : ""
                }`}
              >
                {visibleThumbnails.map((p) => (
                  <ParticipantThumbnail
                    key={`${p.id}-${
                      p.isScreenShareThumbnail ? "screen" : "camera"
                    }`}
                    name={p.name || (agentName as string)}
                    avatarSrc={p.avatar}
                    isMuted={p.isMuted}
                    isVideoOff={p.isVideoOff}
                    isSelf={p.id === userId && !p.isScreenShareThumbnail}
                    mediaStream={p.mediaStream}
                    isScreenShareThumbnail={p.isScreenShareThumbnail}
                    isActiveSpeaker={
                      activeSpeakerInfo?.id === p.id &&
                      !p.isScreenShareThumbnail
                    }
                  />
                ))}
                {/* <ParticipantThumbnail
              key="self-video"
              name="You"
              avatarSrc={localParticipant.avatar}
              isMuted={!localMicOn}
              isVideoOff={!localVideoOn}
              isSelf
              mediaStream={localStream}
              isScreenShareThumbnail={false}
              isActiveSpeaker={false}
            /> */}

                {/* {hiddenParticipantsCount > 0 && (
              <div className="relative md:w-full w-36 h-36 aspect-video rounded-lg overflow bg-gray-800 shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-700 transition-colors">
                <Button
                  className="w-full h-full text-white text-3xl font-bold bg-transparent hover:bg-gray-700/50"
                  onClick={handleOpenParticipantsList}
                >
                  <div className="flex flex-col items-center gap-1">
                    <h2 className="text-3xl font-bold">
                      +{hiddenParticipantsCount}
                    </h2>
                    {hiddenParticipantsAvatars.map((p) => (
                      <Avatar
                        key={p.id}
                        className="h-8 w-8 border-2 border-gray-800 text-sm font-semibold"
                      >
                        <AvatarImage
                          src={p?.avatar || "https://via.placeholder.com/150"}
                          alt={p.name}
                        />
                        <AvatarFallback>{p.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                </Button>
              </div>
            )} */}
              </div>
            ) : (
              <>
                <div
                  ref={selfVideoRef}
                  className="md:w-64 sm:w-36 h-fit block lg:hidden rounded-lg overflow-hidden shadow-xl border-2 border-green-500 absolute cursor-grab active:cursor-grabbing transform transition-none landscape:hidden"
                  style={{
                    left: selfPosition.x,
                    top: selfPosition.y,
                    touchAction: "none",
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                >
                  {visibleThumbnails.map((p) => (
                    <ParticipantThumbnail
                      key={`${p.id}-${
                        p.isScreenShareThumbnail ? "screen" : "camera"
                      }`}
                      name={p.name}
                      avatarSrc={p.avatar}
                      isMuted={p.isMuted}
                      isVideoOff={p.isVideoOff}
                      isSelf={p.id === userId && !p.isScreenShareThumbnail}
                      mediaStream={p.mediaStream}
                      isScreenShareThumbnail={p.isScreenShareThumbnail}
                      isActiveSpeaker={
                        activeSpeakerInfo?.id === p.id &&
                        !p.isScreenShareThumbnail
                      }
                    />
                  ))}
                </div>
                <div className="portrait:hidden md:w-64 w-36 h-auto">
                  {visibleThumbnails.map((p) => (
                    <ParticipantThumbnail
                      key={`${p.id}-${
                        p.isScreenShareThumbnail ? "screen" : "camera"
                      }`}
                      name={p.name || (agentName as string)}
                      avatarSrc={p.avatar}
                      isMuted={p.isMuted}
                      isVideoOff={p.isVideoOff}
                      isSelf={p.id === userId && !p.isScreenShareThumbnail}
                      mediaStream={p.mediaStream}
                      isScreenShareThumbnail={p.isScreenShareThumbnail}
                      isActiveSpeaker={
                        activeSpeakerInfo?.id === p.id &&
                        !p.isScreenShareThumbnail
                      }
                    />
                  ))}
                </div>
              </>
            )
          ) : (
            // DESKTOP: Always show normal thumbnails
            <div
              className={`md:col-span-1 flex md:flex-col gap-3 md:h-auto h-44 overflow-y-auto pr-2 rounded-xl 
                `}
            >
              {visibleThumbnails.map((p) => (
                <ParticipantThumbnail
                  key={`${p.id}-${
                    p.isScreenShareThumbnail ? "screen" : "camera"
                  }`}
                  name={p.name || (agentName as string)}
                  avatarSrc={p.avatar}
                  isMuted={p.isMuted}
                  isVideoOff={p.isVideoOff}
                  isSelf={p.id === userId && !p.isScreenShareThumbnail}
                  mediaStream={p.mediaStream}
                  isScreenShareThumbnail={p.isScreenShareThumbnail}
                  isActiveSpeaker={
                    activeSpeakerInfo?.id === p.id && !p.isScreenShareThumbnail
                  }
                />
              ))}
            </div>
          )}

          {isChatOpen ? (
            <div className="hidden lg:block md:col-span-1 flex-col rounded-xl shadow-md bg-gray-50 flex-grow min-h-0">
              <ChatBotPage
                participants={allParticipants}
                localUserId={userId as string}
                onClose={handleOpenChat}
                sendRtcChatMessage={sendRtcChatMessage}
              />
            </div>
          ) : (
            <div className="hidden lg:block md:col-span-1 flex-col overflow-auto rounded-xl shadow-md bg-gray-50 flex-grow min-h-0">
              {isParticipantsListOpen && (
                <ParticipantsListPage
                  participants={allParticipants}
                  localUserId={userId as string}
                  onClose={handleOpenParticipantsList}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {/* Mobile Chat/Participants List */}
      {isChatOpen ? (
        <div className=" block bg-blue-100 lg:hidden pt-0 w-full rounded-xl shadow-md gap-3 absolute top-16  h-fit landscape:h-96 z-10">
          <ChatBotPage
            participants={allParticipants}
            localUserId={userId as string}
            onClose={handleOpenChat}
            sendRtcChatMessage={sendRtcChatMessage}
          />
        </div>
      ) : (
        <div className="w-full h-auto">
          {isParticipantsListOpen ? (
            <div className=" block lg:hidden px-2 pt-0 w-full overflow rounded-xl shadow-md gap-3 absolute top-14 bg-blue-50  min-h-0">
              <ParticipantsListPage
                participants={allParticipants}
                localUserId={userId as string}
                onClose={handleOpenParticipantsList}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
      <div
        className={`absolute bottom-1 lg:left-1/3 left-1/2 -translate-x-1/2 md:landscape:left-1/3  transition-opacity duration-300 ${
          isMobileOrTablet  ? "landscape:-bottom-8 landscape:left-1/3" : ""
        } ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"} `}
      >
        <CallControlsButton
          onToggleMic={toggleLocalMic}
          onToggleVideo={toggleLocalVideo}
          onEndCall={disconnect}
          onToggleScreenShare={handleToggleScreenShare}
          onOpenChat={handleOpenChat}
          isMicOn={localMicOn}
          isVideoOn={localVideoOn}
          isScreenSharing={mainParticipantInView.isScreenSharing}
          unreadMessageCount={unreadMessageCount}
          selectedMic={selectedMic}
          selectedCamera={selectedCamera}
          onDeviceChange={handleDeviceChange}
          onFacingModeChange={handleFacingModeChange}
        />
      </div>
    </div>
  );
};

export default VideoCallUI;

{
  /* {showLocalFloatingView && (
                <div className="absolute bottom-6 right-6 w-32 h-24 sm:w-40 sm:h-30 md:w-48 md:h-36 lg:w-56 lg:h-32 bg-gray-900 rounded-lg overflow-hidden shadow-xl z-10">
                    <ParticipantThumbnail
                        key={localParticipant.id}
                        name={localParticipant.name}
                        avatarSrc={localParticipant.avatar}
                        isMuted={!isLocalMicOn}
                        isVideoOff={!isLocalVideoOn}
                        isSelf={true}
                        localStream={localUserStream}
                    />
                </div>
            )} */
}
{
  /* <ParticipantThumbnail
              name="You"
              avatarSrc={mainParticipant.avatar} // Use a placeholder avatar for self if camera is off
              isMuted={!localUserStream?.getAudioTracks()[0]?.enabled} // Reflect actual mic state
              isVideoOff={!localUserStream?.getVideoTracks()[0]?.enabled} // Reflect actual video state
              isSelf={true}
              localStream={localUserStream} // Pass your local stream to your own thumbnail
            />

            {screenShareStream || DUMMY_REMOTE_PARTICIPANTS.length > 1 ? (
              <>
                {participantsToShowInThumbnails.map((p) => (
                  <ParticipantThumbnail
                    key={p.id}
                    name={p.name}
                    avatarSrc={p.avatar}
                    isMuted={p.isMuted}
                    isVideoOff={p.isVideoOff}
                    // In a real app, you would pass p.remoteStream here
                    // remoteStream={p.remoteStream}
                  />
                ))}
              </>
            ) : (
              <></>
            )}
            
              <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-800 shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-700 transition-colors">
                <Button>

                <span className="text-white text-3xl font-bold">166{participantsToShowInThumbnails.length}</span>
                </Button>
              </div> */
}

{
  /* People in the background of main view - adjust positioning if needed */
}
{
  /* <div className="absolute bottom-4 right-4 flex space-x-2">
              <div className="w-24 h-16 bg-gray-600 rounded-md overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1517487881594-2787fef5ee4f?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="background participant 1"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-24 h-16 bg-gray-600 rounded-md overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1888&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="background participant 2"
                  className="w-full h-full object-cover"
                />
              </div>
            </div> */
}
